import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Filter, ArrowLeft } from 'lucide-react';
import { capaSourceLabel, capaTypeLabel, capaAssigneeLabel } from '../../utils/capaLabels';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermissions } from '../../context/PermissionContext';
import { formatDate } from '../../utils/dateUtils';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAPAList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [capas, setCapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    severity: ''
  });

  useEffect(() => {
    fetchCAPAs();
  }, [filters]);

  const fetchCAPAs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);
      
      const response = await axios.get(`${API}/capa?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapas(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load CAPAs');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Assigned': 'bg-blue-100 text-blue-800',
      'Pending Approval': 'bg-orange-100 text-orange-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Pending Verification': 'bg-purple-100 text-purple-800',
      'Verified': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Closed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="capa-list">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      {/* Back Button */}
      <Button
        variant="outline"
        onClick={() => navigate('/safety')}
        className="mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">CAPA Management</h1>
          <p className="text-gray-600 mt-1">Corrective & Preventive Actions</p>
        </div>
        {hasPermission('capa', 'create') && (
          <Button onClick={() => navigate('/capa/create')} data-testid="create-capa-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create CAPA
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value === 'all' ? '' : value})}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Pending Approval">Pending Approval</SelectItem>
                <SelectItem value="Assigned">Assigned</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Pending Verification">Pending Verification</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.severity} onValueChange={(value) => setFilters({...filters, severity: value === 'all' ? '' : value})}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={() => setFilters({ status: '', severity: '' })}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* CAPA Cards */}
      <div className="space-y-4">
        {capas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No CAPAs found matching the filters</p>
            </CardContent>
          </Card>
        ) : (
          capas.map((capa) => (
            <Card 
              key={capa.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/capa/${capa.id}`)}
              data-testid={`capa-card-${capa.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{capa.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(capa.severity)}`}>
                        {capa.severity}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(capa.status)}`}>
                        {capa.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{capa.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <span className="ml-1 font-medium">{capaTypeLabel(capa.capa_type)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Source:</span>
                        <span className="ml-1 font-medium">{capaSourceLabel(capa.source)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Assigned To:</span>
                        <span className="ml-1 font-medium">{capaAssigneeLabel(capa)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Due Date:</span>
                        <span className="ml-1 font-medium">{formatDate(capa.due_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      </div>
    </div>
  );
};

export default CAPAList;
